import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("sirh_user");
    const token = localStorage.getItem("sirh_token");
    if (stored && token) {
      setUser(JSON.parse(stored));
      api.get("/auth/me").catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("sirh_token");
          localStorage.removeItem("sirh_user");
          setUser(null);
        }
      });
    } else {
      setUser(null);
    }
  }, []);

  const persist = (u) => {
    localStorage.setItem("sirh_token", u.token);
    localStorage.setItem(
      "sirh_user",
      JSON.stringify({ id: u.id, email: u.email, name: u.name, role: u.role, persona: u.persona })
    );
    setUser({ id: u.id, email: u.email, name: u.name, role: u.role, persona: u.persona });
  };

  const login = async (email, password, { remember = true } = {}) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      persist(data.data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const loginAsPersona = async (personaKey, { remember = true } = {}) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/persona-login", { persona_key: personaKey, remember });
      persist(data.data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const loginAsAdmin = async ({ remember = true } = {}) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/admin-quick-login", { remember });
      persist(data.data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      console.warn("Logout non-blocking error:", err?.message);
    }
    localStorage.removeItem("sirh_token");
    localStorage.removeItem("sirh_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, loginAsPersona, loginAsAdmin, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
